import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { exportRFIs } from '@/utils/exportUtils';
import { RFIActionModal } from '@/components/RFIActionModal';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RFIsTabEnhanced = ({ projectId }) => {
  const [rfis, setRfis] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRfi, setSelectedRfi] = useState(null);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', specification_section: '', drawing_reference: '',
    assigned_to: '', due_date: '', priority: 'medium', cost_impact: 'none', schedule_impact: 'none'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchRfis();
    fetchUsers();
  }, []);

  const fetchRfis = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/rfis`);
      setRfis(response.data);
    } catch (error) {
      toast.error('Failed to load RFIs');
    }
  };

  const fetchUsers = async () => {
    try {
      // Use project-specific users endpoint for non-admin users
      const response = await axios.get(`${API}/projects/${projectId}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const uploadFile = async (rfiId) => {
    if (!selectedFile) return null;
    
    setUploading(true);
    const fileData = new FormData();
    fileData.append('file', selectedFile);
    fileData.append('document_type', 'rfis');
    fileData.append('project_id', projectId);
    
    try {
      const response = await axios.post(`${API}/upload`, fileData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { url: response.data.file_url, name: response.data.file_name || selectedFile.name };
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('File upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const createRfi = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/projects/${projectId}/rfis`, formData);
      const rfiId = response.data.id;
      
      // Upload file if selected
      if (selectedFile) {
        const fileData = await uploadFile(rfiId);
        if (fileData) {
          await axios.patch(`${API}/rfis/${rfiId}`, null, {
            params: { 
              file_url: fileData.url, 
              file_name: fileData.name 
            }
          });
        }
      }
      
      toast.success('RFI created!');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', specification_section: '', drawing_reference: '', assigned_to: '', due_date: '', priority: 'medium', cost_impact: 'none', schedule_impact: 'none' });
      setSelectedFile(null);
      fetchRfis();
    } catch (error) {
      toast.error('Failed to create RFI');
    }
  };

  const calculateDaysOverdue = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const formatDateMMDDYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  const deleteRfi = async (rfiId, rfiTitle) => {
    if (!window.confirm(`Are you sure you want to delete RFI "${rfiTitle}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/projects/${projectId}/rfis/${rfiId}`);
      toast.success('RFI deleted successfully');
      fetchRfis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete RFI');
    }
  };

  return (
    <div className="space-y-4" data-testid="rfis-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">RFIs (Requests for Information)</h2>
          <p className="text-sm text-slate-600 mt-1">Track project questions and clarifications</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportRFIs(rfis, 'excel')} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm" disabled={rfis.length === 0}>Export Excel</button>
          <button onClick={() => exportRFIs(rfis, 'pdf')} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm" disabled={rfis.length === 0}>Export PDF</button>
          <button onClick={() => setShowCreateModal(true)} className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800" data-testid="create-rfi-button">Create RFI</button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
        <table className="w-full" data-testid="rfis-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">RFI #</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Raised By</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned To</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Priority</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Closed Date</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Days Overdue</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Response</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rfis.map((rfi, index) => {
              const overdue = calculateDaysOverdue(rfi.due_date);
              const isInitiator = user?.id === rfi.raised_by;
              const isAssignee = user?.id === rfi.assigned_to;
              const isPendingMoreInfo = rfi.status === 'more_info_needed';
              const currentAssigneeName = rfi.current_assignee_name || rfi.assigned_to_name;
              
              // Can take action
              const canTakeAction = user?.role === 'admin' || 
                                   (isAssignee && !isPendingMoreInfo) ||
                                   (isInitiator && isPendingMoreInfo);
              
              return (
                <tr key={rfi.id} className="border-b hover:bg-slate-50" data-testid={`rfi-row-${rfi.id}`}>
                  <td className="p-4 font-mono font-medium">#{String(index + 1).padStart(3, '0')}</td>
                  <td className="p-4 font-medium">{rfi.title}</td>
                  <td className="p-4 text-sm">{rfi.raised_by_name || 'Unknown'}</td>
                  <td className="p-4 text-sm">
                    <div>
                      <span>{rfi.assigned_to_name || 'Unknown'}</span>
                      {isPendingMoreInfo && (
                        <div className="text-xs text-amber-600 mt-0.5">
                          Awaiting: {rfi.raised_by_name}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      rfi.priority === 'high' ? 'bg-red-100 text-red-700' :
                      rfi.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {rfi.priority}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      rfi.status === 'closed' ? 'bg-green-100 text-green-700' :
                      rfi.status === 'more_info_needed' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {rfi.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-4 text-sm" data-testid={`rfi-closed-date-${rfi.id}`}>
                    {formatDateMMDDYY(rfi.closed_date)}
                  </td>
                  <td className="p-4">
                    {rfi.status !== 'closed' && overdue > 0 ? (
                      <span className="text-red-600 font-semibold text-sm">{overdue} days</span>
                    ) : (
                      <span className="text-green-600 text-sm">{rfi.status === 'closed' ? '-' : 'On time'}</span>
                    )}
                  </td>
                  <td className="p-4 text-sm">
                    {rfi.response ? (
                      <div>
                        <div className="max-w-xs truncate">{rfi.response}</div>
                        {rfi.response_by_name && (
                          <div className="text-xs text-slate-500">by {rfi.response_by_name}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                    {(rfi.attachments?.length > 0) && (
                      <span className="text-xs text-slate-500 ml-1">
                        ({rfi.attachments.length} files)
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => { setSelectedRfi(rfi); setShowActionModal(true); }} 
                        className={`text-sm hover:underline font-medium ${
                          canTakeAction ? 'text-blue-600' : 'text-slate-500'
                        }`}
                        data-testid={`rfi-action-${rfi.id}`}
                      >
                        {canTakeAction 
                          ? (isPendingMoreInfo && isInitiator ? 'Respond' : 'Take Action')
                          : 'View'
                        }
                      </button>
                      {(user?.role === 'admin' || rfi.raised_by === user?.id) && (
                        <button 
                          onClick={() => deleteRfi(rfi.id, rfi.title)}
                          className="text-sm text-red-600 hover:underline font-medium"
                          data-testid={`delete-rfi-${rfi.id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Create RFI</h2>
            <form onSubmit={createRfi} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">RFI Title *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Question / Description *</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" rows="4" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Specification Section</label>
                  <input type="text" value={formData.specification_section} onChange={(e) => setFormData({...formData, specification_section: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" placeholder="e.g., 03 30 00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Drawing Reference</label>
                  <input type="text" value={formData.drawing_reference} onChange={(e) => setFormData({...formData, drawing_reference: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" placeholder="e.g., A-101" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Assign To *</label>
                  <select value={formData.assigned_to} onChange={(e) => setFormData({...formData, assigned_to: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required>
                    <option value="">Select user</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Due Date *</label>
                  <input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cost Impact</label>
                  <select value={formData.cost_impact} onChange={(e) => setFormData({...formData, cost_impact: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md">
                    <option value="none">None</option>
                    <option value="tbd">TBD</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Schedule Impact</label>
                  <select value={formData.schedule_impact} onChange={(e) => setFormData({...formData, schedule_impact: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md">
                    <option value="none">None</option>
                    <option value="tbd">TBD</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Attach File (Optional)</label>
                  <input 
                    type="file" 
                    onChange={handleFileSelect} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.dwg,.dxf"
                    data-testid="rfi-file-input"
                  />
                  {selectedFile && (
                    <p className="text-xs text-slate-600 mt-1">Selected: {selectedFile.name}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">Supported: PDF, Word, Excel, Images, CAD files</p>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" disabled={uploading} className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800 disabled:opacity-50">
                  {uploading ? 'Uploading...' : 'Create RFI'}
                </button>
                <button type="button" onClick={() => { setShowCreateModal(false); setSelectedFile(null); }} className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showActionModal && selectedRfi && (
        <RFIActionModal
          rfi={selectedRfi}
          onClose={() => setShowActionModal(false)}
          onUpdate={fetchRfis}
        />
      )}
    </div>
  );
};